import React, { useEffect, useState, useCallback } from "react";
import { passwordError, PASSWORD_HINT } from "@/lib/passwordPolicy";
import { useNavigate } from "react-router-dom";
import { Search, Trash2, Plus, X, Briefcase, UserCheck, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";
import Pagination from "@/components/Pagination";
import { HotelMultiPicker } from "@/components/HotelMultiPicker";

const PAGE_SIZE = 50;

const STAFF_ROLES = [
    { v: "super_admin", l: "Süper Admin", desc: "Tüm yetkiler", c: "bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300", group: "admin" },
    { v: "admin", l: "Admin", desc: "Tüm yetkiler (varsayılan)", c: "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300", group: "admin" },
    { v: "operations", l: "Operasyon", desc: "Rezervasyon, ödeme, voucher", c: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300", group: "admin" },
    { v: "finance", l: "Muhasebe / Finans", desc: "Finans + rezervasyon (read)", c: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300", group: "admin" },
    { v: "content", l: "İçerik / Otel Yönetimi", desc: "Otel, oda, fiyat, takvim", c: "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300", group: "admin" },
    // Hotel extranet roles — require a hotel
    { v: "otel_owner", l: "Otel Sahibi (Extranet)", desc: "Otelin tüm extranet yetkileri", c: "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300", group: "hotel", requiresHotel: true },
    { v: "otel_manager", l: "Otel Yöneticisi (Extranet)", desc: "Rezervasyon + fiyat + envanter", c: "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300", group: "hotel", requiresHotel: true },
    { v: "otel_reception", l: "Resepsiyon (Extranet)", desc: "Sadece bugün + rezervasyon listesi", c: "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300", group: "hotel", requiresHotel: true },
    { v: "otel_accounting", l: "Otel Muhasebe (Extranet)", desc: "Finans + raporlar", c: "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300", group: "hotel", requiresHotel: true },
];
const STAFF_ROLE_VALUES = STAFF_ROLES.map((r) => r.v);

const CUSTOMER_ROLE = { v: "customer", l: "Müşteri", c: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" };

const ALL_ROLES = [...STAFF_ROLES, CUSTOMER_ROLE];

export default function AdminUsersPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [tab, setTab] = useState("staff"); // staff | customer
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [assignUser, setAssignUser] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        params.set("role", tab); // staff or customer
        params.set("limit", String(PAGE_SIZE));
        params.set("skip", String((page - 1) * PAGE_SIZE));
        api.get(`/admin/users?${params.toString()}`)
            .then((r) => { setItems(r.data.items); setTotal(r.data.total); })
            .finally(() => setLoading(false));
    }, [q, tab, page]);

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/login");
        load();
    }, [user, authLoading, load, navigate]);

    useEffect(() => {
        const t = setTimeout(load, 350);
        return () => clearTimeout(t);
    }, [q, tab, page, load]);

    // Sekme veya arama değişince sayfa 1'e dön
    useEffect(() => { setPage(1); }, [q, tab]);

    const updateUser = async (uid, payload) => {
        try {
            const r = await api.patch(`/admin/users/${uid}`, payload);
            setItems((prev) => prev.map((x) => x.id === uid ? { ...x, ...r.data } : x));
            toast.success("Güncellendi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Güncelleme başarısız");
        }
    };

    const onDelete = async (uid, email) => {
        if (!window.confirm(`'${email}' kullanıcısını silmek istiyor musunuz?`)) return;
        try {
            await api.delete(`/admin/users/${uid}`);
            setItems((prev) => prev.filter((x) => x.id !== uid));
            toast.success("Silindi");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silme başarısız");
        }
    };

    const onResetPassword = async (uid, email) => {
        const pwd = window.prompt(`'${email}' için yeni şifre giriniz (${PASSWORD_HINT}):`);
        if (!pwd) return;
        if (passwordError(pwd)) { toast.error(passwordError(pwd)); return; }
        if (!window.confirm(`'${email}' kullanıcısının şifresi şu yeni şifreyle güncellenecek:\n\n${pwd}\n\nDevam edilsin mi?`)) return;
        try {
            await api.post(`/admin/users/${uid}/reset-password`, { new_password: pwd });
            toast.success(`${email} şifresi sıfırlandı`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Şifre sıfırlama başarısız");
        }
    };

    const saveHotels = async (uid, hotel_ids) => {
        try {
            await api.patch(`/admin/users/${uid}`, { hotel_ids });
            toast.success("Otel yetkileri güncellendi");
            setAssignUser(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Güncelleme başarısız");
        }
    };

    const createStaff = async (payload) => {
        try {
            const r = await api.post("/admin/users/staff", payload);
            setItems((prev) => [r.data, ...prev]);
            setTotal((t) => t + 1);
            setShowCreate(false);
            toast.success(`${payload.full_name} oluşturuldu`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Oluşturulamadı");
        }
    };

    return (
        <AdminLayout
            title="Kullanıcılar"
            action={tab !== "customer" && (
                <button data-testid="add-staff-btn" onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold">
                    <Plus className="h-4 w-4" /> {tab === "hotel" ? "Extranet Hesabı Ekle" : "Çalışan Ekle"}
                </button>
            )}
        >
            {/* Tabs */}
            <div className="flex gap-1 mb-5 p-1 rounded-2xl bg-slate-100 dark:bg-slate-900 inline-flex">
                <TabButton active={tab === "staff"} onClick={() => setTab("staff")} icon={<Briefcase className="h-4 w-4" />} label="Admin Çalışanları" testid="tab-staff" />
                <TabButton active={tab === "hotel"} onClick={() => setTab("hotel")} icon={<ShieldCheck className="h-4 w-4" />} label="Otel Extranet" testid="tab-hotel" />
                <TabButton active={tab === "customer"} onClick={() => setTab("customer")} icon={<UserCheck className="h-4 w-4" />} label="Üyeler" testid="tab-customer" />
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-5">
                <div className="flex items-center gap-2 flex-1 min-w-[280px] px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                    <Search className="h-4 w-4 text-slate-400" />
                    <input data-testid="user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="İsim veya e-posta..." className="w-full bg-transparent outline-none text-sm text-slate-900 dark:text-white" />
                </div>
                <span className="text-sm text-slate-500">{total} {tab === "staff" ? "admin çalışanı" : tab === "hotel" ? "extranet kullanıcısı" : "üye"}</span>
            </div>

            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">
                                <th className="py-3 px-4">Kullanıcı</th>
                                <th className="py-3 px-4">E-posta</th>
                                <th className="py-3 px-4">{tab === "customer" ? "Tip" : "Rol"}</th>
                                {tab === "hotel" && <th className="py-3 px-4">Otel</th>}
                                {tab === "customer" && <th className="py-3 px-4">Rezervasyon</th>}
                                <th className="py-3 px-4">Durum</th>
                                <th className="py-3 px-4">Kayıt</th>
                                <th className="py-3 px-4 text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? <tr><td colSpan="8" className="py-8 text-center text-slate-500">Yükleniyor...</td></tr>
                                : items.length === 0 ? <tr><td colSpan="8" className="py-8 text-center text-slate-500">Bu sekmede henüz kullanıcı yok.</td></tr>
                                : items.map((u) => {
                                    const roleObj = ALL_ROLES.find((r) => r.v === u.role) || { l: u.role, c: "bg-slate-100 text-slate-700" };
                                    return (
                                        <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100 dark:border-slate-800/40 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-sky-500 to-amber-400 flex items-center justify-center text-white font-bold">{u.full_name?.[0] || "?"}</div>
                                                    <div className="font-semibold text-slate-900 dark:text-white">{u.full_name}</div>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{u.email}</td>
                                            <td className="py-3 px-4">
                                                {tab === "staff" ? (
                                                    <select data-testid={`role-select-${u.id}`} value={u.role} onChange={(e) => updateUser(u.id, { role: e.target.value })} className="px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-900 dark:text-white">
                                                        {STAFF_ROLES.filter((r) => r.group === "admin").map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                                                    </select>
                                                ) : tab === "hotel" ? (
                                                    <select data-testid={`role-select-${u.id}`} value={u.role} onChange={(e) => updateUser(u.id, { role: e.target.value })} className="px-2 py-1 rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 text-xs font-semibold text-slate-900 dark:text-white">
                                                        {STAFF_ROLES.filter((r) => r.group === "hotel").map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                                                    </select>
                                                ) : (
                                                    <span className={`text-[10px] font-bold px-2 py-1 rounded-md inline-block ${roleObj.c}`}>{roleObj.l}</span>
                                                )}
                                            </td>
                                            {tab === "hotel" && (
                                                <td className="py-3 px-4 text-xs">
                                                    <div className="flex flex-wrap items-center gap-1 max-w-[320px]">
                                                        {(u.hotels || []).length === 0 && <span className="text-rose-500 italic">— atama yok —</span>}
                                                        {(u.hotels || []).map((h) => (
                                                            <span key={h.id} className={`px-2 py-0.5 rounded-md font-semibold ${h.id === u.hotel_id ? "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300" : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}>{h.name}</span>
                                                        ))}
                                                        <button data-testid={`user-edit-hotels-${u.id}`} onClick={() => setAssignUser(u)} className="px-2 py-0.5 rounded-md border border-dashed border-rose-300 text-rose-600 font-semibold hover:bg-rose-50 dark:hover:bg-rose-950/30">Düzenle</button>
                                                    </div>
                                                </td>
                                            )}
                                            {tab === "customer" && <td className="py-3 px-4 text-slate-700 dark:text-slate-300">{u.booking_count}</td>}
                                            <td className="py-3 px-4">
                                                <button
                                                    data-testid={`toggle-active-${u.id}`}
                                                    onClick={() => updateUser(u.id, { is_active: !u.is_active })}
                                                    className={`text-xs font-bold px-2 py-1 rounded-md ${u.is_active === false ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}
                                                >
                                                    {u.is_active === false ? "Pasif" : "Aktif"}
                                                </button>
                                            </td>
                                            <td className="py-3 px-4 text-xs text-slate-500">{formatDate(u.created_at)}</td>
                                            <td className="py-3 px-4 text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    <button
                                                        data-testid={`user-reset-password-${u.id}`}
                                                        onClick={() => onResetPassword(u.id, u.email)}
                                                        className="p-1.5 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-950/30 text-amber-600"
                                                        title="Şifreyi Sıfırla"
                                                    >
                                                        <KeyRound className="h-4 w-4" />
                                                    </button>
                                                    {u.email !== user?.email && (
                                                        <button
                                                            data-testid={`user-delete-${u.id}`}
                                                            onClick={() => onDelete(u.id, u.email)}
                                                            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600"
                                                            title="Sil"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>
                <Pagination total={total} page={page} pageSize={PAGE_SIZE} onChange={setPage} label={tab === "staff" ? "çalışan" : "üye"} />
            </div>

            {showCreate && <StaffCreateModal mode={tab === "hotel" ? "hotel" : "staff"} onCancel={() => setShowCreate(false)} onSubmit={createStaff} />}
            {assignUser && <HotelAssignModal u={assignUser} onCancel={() => setAssignUser(null)} onSave={(ids) => saveHotels(assignUser.id, ids)} />}
        </AdminLayout>
    );
}

function TabButton({ active, onClick, icon, label, testid }) {
    return (
        <button data-testid={testid} onClick={onClick} className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${active ? "bg-white dark:bg-slate-800 shadow text-slate-900 dark:text-white" : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}>
            {icon} {label}
        </button>
    );
}

function HotelAssignModal({ u, onCancel, onSave }) {
    const [ids, setIds] = useState(u.hotel_ids || []);
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onCancel}>
            <div data-testid="hotel-assign-modal" className="w-full max-w-lg bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                    <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">Otel Yetkileri</h2>
                    <div className="text-xs text-slate-500">{u.full_name} · {u.email}</div>
                </div>
                <div className="p-6">
                    <HotelMultiPicker value={ids} onChange={setIds} />
                    <div className="text-[11px] text-slate-500 mt-2">Kullanıcı yalnızca seçili otellerde işlem yapabilir. Birden fazla otel seçilirse extranet'te oteller arasında geçiş yapar.</div>
                </div>
                <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2">
                    <button onClick={onCancel} className="px-5 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-semibold">İptal</button>
                    <button data-testid="hotel-assign-save" disabled={ids.length === 0} onClick={() => onSave(ids)} className="px-6 py-2 rounded-xl bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white font-bold text-sm">Kaydet</button>
                </div>
            </div>
        </div>
    );
}

function RoleOption({ r, active, onSelect, accent }) {
    return (
        <label data-testid={`staff-role-${r.v}`} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${active ? (accent === "rose" ? "bg-rose-50 dark:bg-rose-950/30 border-rose-500" : "bg-sky-50 dark:bg-sky-950/30 border-sky-500") : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 hover:border-slate-300"}`}>
            <input type="radio" name="role" checked={active} onChange={onSelect} className={`h-4 w-4 ${accent === "rose" ? "accent-rose-500" : "accent-sky-500"}`} />
            <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-slate-900 dark:text-white">{r.l}</div>
                <div className="text-xs text-slate-500">{r.desc}</div>
            </div>
        </label>
    );
}

function StaffCreateModal({ mode, onCancel, onSubmit }) {
    const isHotel = mode === "hotel";
    const [form, setForm] = useState({ full_name: "", email: "", password: "", role: isHotel ? "otel_owner" : "operations" });
    const [hotelIds, setHotelIds] = useState([]);
    const roles = STAFF_ROLES.filter((r) => r.group === (isHotel ? "hotel" : "admin"));

    const submit = () => {
        if (!form.full_name || !form.email || !form.password) { toast.error("Ad, e-posta ve şifre zorunlu"); return; }
        if (passwordError(form.password)) { toast.error(passwordError(form.password)); return; }
        if (isHotel && hotelIds.length === 0) { toast.error("Lütfen en az bir otel seçin"); return; }
        onSubmit(isHotel ? { ...form, hotel_ids: hotelIds } : form);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onCancel}>
            <div data-testid="staff-create-modal" className="w-full max-w-xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
                    <div className="flex items-center gap-2">
                        <ShieldCheck className={`h-5 w-5 ${isHotel ? "text-rose-500" : "text-sky-500"}`} />
                        <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">{isHotel ? "Yeni Extranet Hesabı" : "Yeni Çalışan Ekle"}</h2>
                    </div>
                    <button onClick={onCancel} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                </div>
                <div className="p-6 space-y-3">
                    {isHotel && (
                        <div>
                            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Yetkili Olacağı Otel(ler) *</div>
                            <HotelMultiPicker value={hotelIds} onChange={setHotelIds} />
                            <div className="text-[11px] text-slate-500 mt-1">Kullanıcı yalnızca seçtiğiniz otellerin extranet'ine erişebilir.</div>
                        </div>
                    )}
                    <Field label="Ad Soyad"><input data-testid="staff-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={INPUT} /></Field>
                    <Field label="E-posta"><input data-testid="staff-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={INPUT} /></Field>
                    <Field label="Şifre"><input data-testid="staff-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={INPUT} placeholder={PASSWORD_HINT} /></Field>
                    <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{isHotel ? "Extranet Rolü" : "Admin Panel Rolü"}</div>
                        <div className="space-y-2">
                            {roles.map((r) => <RoleOption key={r.v} r={r} active={form.role === r.v} onSelect={() => setForm({ ...form, role: r.v })} accent={isHotel ? "rose" : "sky"} />)}
                        </div>
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2 sticky bottom-0 bg-white dark:bg-slate-900">
                    <button onClick={onCancel} className="px-5 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-semibold">İptal</button>
                    <button data-testid="staff-submit" onClick={submit} className={`px-6 py-2 rounded-xl text-white font-bold text-sm ${isHotel ? "bg-rose-500 hover:bg-rose-600" : "bg-sky-500 hover:bg-sky-600"}`}>Kullanıcıyı Oluştur</button>
                </div>
            </div>
        </div>
    );
}

const INPUT = "w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white";

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>
            {children}
        </label>
    );
}
